/**
 * colorUtils.js
 * 品牌色彩工具函數
 * 從 hex 計算 HSL 色彩變體，用於品牌頁動態配色系統
 */

const DEFAULT_BRAND_COLOR = '#3366FF'

/**
 * 將 hex 色碼轉換為 HSL 分量
 * @param {string} hex - 如 '#3366FF'
 * @returns {{ h: number, s: number, l: number }} HSL 分量 (h: 0-360, s: 0-100, l: 0-100)
 */
export function hexToHsl(hex) {
  const clean = hex.replace(/^#/, '')
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case r: h = ((g - b) / delta) % 6; break
      case g: h = (b - r) / delta + 2; break
      case b: h = (r - g) / delta + 4; break
    }
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }

  return { h, s: Math.round(s * 100), l: Math.round(l * 100) }
}

/**
 * 將 HSL 分量轉為 hex 色碼
 * @param {number} h - 0-360
 * @param {number} s - 0-100
 * @param {number} l - 0-100
 * @returns {string} hex 色碼，如 '#3366FF'
 */
export function hslToHex(h, s, l) {
  const sNorm = s / 100
  const lNorm = l / 100
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = lNorm - c / 2
  let r = 0, g = 0, b = 0

  if (h < 60)      { r = c; g = x; b = 0 }
  else if (h < 120) { r = x; g = c; b = 0 }
  else if (h < 180) { r = 0; g = c; b = x }
  else if (h < 240) { r = 0; g = x; b = c }
  else if (h < 300) { r = x; g = 0; b = c }
  else              { r = c; g = 0; b = x }

  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * 驗證 hex 色碼格式是否正確
 * @param {string} hex
 * @returns {boolean}
 */
export function isValidHex(hex) {
  return /^#([0-9A-Fa-f]{6})$/.test(hex)
}

/**
 * 計算相對亮度（WCAG 2.1）
 * @param {string} hex
 * @returns {number} 0 (最暗) 到 1 (最亮)
 */
export function getRelativeLuminance(hex) {
  const clean = hex.replace(/^#/, '')
  const channels = [
    parseInt(clean.slice(0, 2), 16) / 255,
    parseInt(clean.slice(2, 4), 16) / 255,
    parseInt(clean.slice(4, 6), 16) / 255
  ]
  return channels.reduce((sum, c) => {
    const linear = c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    return sum + linear * [0.2126, 0.7152, 0.0722][channels.indexOf(c)]
  }, 0)
}

/**
 * 計算兩色之間的對比度（WCAG 2.1）
 * @param {string} hex1
 * @param {string} hex2
 * @returns {number} 對比度比值（1-21）
 */
export function getContrastRatio(hex1, hex2) {
  const l1 = getRelativeLuminance(hex1)
  const l2 = getRelativeLuminance(hex2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * 檢查品牌色在白色背景上是否符合 WCAG AA 最低對比度（3:1 for UI components）
 * @param {string} hex
 * @returns {boolean}
 */
export function meetsMinimumContrast(hex) {
  return getContrastRatio(hex, '#FFFFFF') >= 3.0
}

/**
 * 從品牌主色生成完整的色彩系統
 * @param {string} brandColor - hex 色碼，如 '#3366FF'，fallback 為 DEFAULT_BRAND_COLOR
 * @returns {{ primary, primaryDark, primaryLight, primaryGradient, background, text }}
 */
export function generateColorSystem(brandColor) {
  const color = (brandColor && isValidHex(brandColor)) ? brandColor : DEFAULT_BRAND_COLOR
  const { h, s, l } = hexToHsl(color)

  // primaryDark：降低亮度 15%（最低不低於 10）
  const darkL = Math.max(10, l - 15)
  const primaryDark = hslToHex(h, s, darkL)

  // primaryLight：提高亮度 15%（最高不超過 80）
  const lightL = Math.min(80, l + 15)
  const primaryLight = hslToHex(h, s, lightL)

  // background：飽和度降低 60%，亮度提高至 93%
  const bgS = Math.max(0, s - 60)
  const bgL = Math.max(l, 93)
  const background = hslToHex(h, bgS, bgL)

  return {
    primary: color,
    primaryDark,
    primaryLight,
    primaryGradient: `linear-gradient(to right, ${color}, ${primaryLight})`,
    background,
    text: {
      primary: '#222222',
      secondary: '#717171'
    }
  }
}

export { DEFAULT_BRAND_COLOR }
